import { createRouter, createWebHistory } from 'vue-router'

import { useAuthStore } from '@/stores/auth'
import { appEnv } from '@/config/env'
import { resolveOnboardingEntry } from '@/views/onboarding/onboarding-entry'
import { readPortalSession } from '@/utils/storage'
import {
  isSafePortalBackTarget,
  isRouteChunkLoadError,
  replaceWithFreshDocument,
} from '@/utils/navigation'

const ROUTE_CHUNK_RELOAD_KEY = 'member-portal:route-chunk-reload'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      component: () => import('@/views/member/MemberHomeView.vue'),
      meta: { requiresAuth: true, requiresBinding: true, title: '会员中心' },
      name: 'member-home',
      path: '/',
    },
    {
      component: () => import('@/views/service/MemberServiceLandingView.vue'),
      meta: { public: true, title: '专享服务' },
      name: 'member-service',
      path: '/services/:code',
    },
    {
      component: () => import('@/views/service/MemberServicePreviewView.vue'),
      meta: { public: true, title: '服务页面预览' },
      name: 'member-service-preview',
      path: '/preview/service-page',
    },
    {
      component: () => import('@/views/merchant/MerchantListView.vue'),
      meta: { public: true, title: '精选商户' },
      name: 'merchant-list',
      path: '/merchants',
    },
    {
      component: () => import('@/views/merchant/DiningRecommendationListView.vue'),
      meta: { public: true, title: '餐饮推荐列表' },
      name: 'dining-recommendations',
      path: '/services/dining/recommendations',
    },
    {
      component: () => import('@/views/merchant/MerchantDetailView.vue'),
      meta: { public: true, title: '商户详情' },
      name: 'merchant-detail',
      path: '/merchants/:id',
    },
    {
      component: () => import('@/views/member/MemberProfileView.vue'),
      meta: { requiresAuth: true, requiresBinding: true, title: '会员资料' },
      name: 'member-profile',
      path: '/member/profile',
    },
    {
      component: () => import('@/views/order/MemberOrderListView.vue'),
      meta: { requiresAuth: true, requiresBinding: true, title: '我的订单' },
      name: 'member-orders',
      path: '/member/orders',
    },
    {
      component: () => import('@/views/record/MemberConsumptionRecordView.vue'),
      meta: { requiresAuth: true, requiresBinding: true, title: '消费记录' },
      name: 'member-consumption-records',
      path: '/member/records',
    },
    ...(import.meta.env.DEV
      ? [{
          component: () => import('@/views/record/MemberConsumptionRecordView.vue'),
          meta: { public: true, title: '消费记录本地预览' },
          name: 'member-consumption-records-preview',
          path: '/preview/member-records',
          props: (route: { query: Record<string, unknown> }) => ({
            previewLevel: route.query.level === 'platinum' ? 'platinum' : 'diamond',
          }),
        }, {
          component: () => import('@/views/order/MemberOrderDetailView.vue'),
          meta: { public: true, title: '订单详情本地预览' },
          name: 'member-order-detail-preview',
          path: '/preview/member-order-detail',
          props: { preview: true },
        }]
      : []),
    {
      component: () => import('@/views/order/MemberOrderDetailView.vue'),
      meta: { requiresAuth: true, requiresBinding: true, title: '订单详情' },
      name: 'member-order-detail',
      path: '/member/orders/:id',
    },
    {
      component: () => import('@/views/bill/MemberBillListView.vue'),
      meta: { requiresAuth: true, requiresBinding: true, title: '我的账单' },
      name: 'member-bills',
      path: '/bills',
    },
    {
      component: () => import('@/views/bill/MemberBillDetailView.vue'),
      meta: { requiresAuth: true, requiresBinding: true, title: '账单详情' },
      name: 'member-bill-detail',
      path: '/bills/:id',
    },
    ...(import.meta.env.DEV
      ? [{
          component: () => import('@/views/bill/MemberBillLegacyPreviewView.vue'),
          meta: { public: true, title: '旧版月账单本地预览' },
          name: 'member-bill-legacy-preview',
          path: '/preview/member-bill-legacy',
        }]
      : []),
    {
      component: () => import('@/views/bill/MemberBillA1TemplateView.vue'),
      meta: { requiresAuth: true, requiresBinding: true, title: 'A1 月账单' },
      name: 'member-bill-a1-template',
      path: '/bill-templates/a1/:id',
    },
    {
      component: () => import('@/views/bill/MemberAnnualBillView.vue'),
      meta: { requiresAuth: true, requiresBinding: true, title: '年度账单' },
      name: 'member-annual-bill',
      path: '/annual-bills/:year',
    },
    {
      component: () => import('@/views/benefit/MemberBenefitsView.vue'),
      meta: { requiresAuth: true, requiresBinding: true, title: '我的权益-专属管家' },
      name: 'member-benefits',
      path: '/benefits',
    },
    {
      component: () => import('@/views/safebox/MemberSafeBoxView.vue'),
      meta: { requiresAuth: true, requiresBinding: true, title: '我的保险柜' },
      name: 'member-safe-box',
      path: '/safe-box',
    },
    {
      component: () => import('@/views/points/MemberPointsView.vue'),
      meta: { requiresAuth: true, requiresBinding: true, title: '我的积分' },
      name: 'member-points',
      path: '/points',
    },
    {
      component: () => import('@/views/balance/MemberBalanceView.vue'),
      meta: { requiresAuth: true, requiresBinding: true, title: '账户余额' },
      name: 'member-balance',
      path: '/balance',
    },
    {
      component: () => import('@/views/payment/MemberPaymentResultView.vue'),
      meta: { requiresAuth: true, requiresBinding: true, title: '支付结果' },
      name: 'member-payment-result',
      path: '/payment/result',
    },
    {
      component: () => import('@/views/balance/MemberRechargeHistoryView.vue'),
      meta: { requiresAuth: true, requiresBinding: true, title: '充值记录' },
      name: 'member-recharges',
      path: '/balance/recharges',
    },
    {
      component: () => import('@/views/balance/MemberBankLimitGuideView.vue'),
      meta: { requiresAuth: true, requiresBinding: true, title: '银行卡限额查询指引' },
      name: 'member-bank-limits',
      path: '/balance/bank-limits',
    },
    {component:()=>import('@/views/balance/MemberCashbackView.vue'),meta:{requiresAuth:true,requiresBinding:true,title:'返现记录'},name:'member-cashbacks',path:'/balance/cashbacks'},
    {
      component: () => import('@/views/activity/MemberActivityListView.vue'),
      meta: { requiresAuth: true, requiresBinding: true, title: '会员活动' },
      name: 'member-activities',
      path: '/activities',
    },
    { component:()=>import('@/views/points/MemberPointsShopView.vue'), meta:{requiresAuth:true,requiresBinding:true,title:'积分商城'}, name:'member-points-shop', path:'/points-shop' },
    { component:()=>import('@/views/points/MemberPointsProductDetailView.vue'), meta:{requiresAuth:true,requiresBinding:true,title:'礼遇详情'}, name:'member-points-product-detail', path:'/points-shop/products/:id' },
    { component:()=>import('@/views/points/MemberPointsCheckoutView.vue'), meta:{requiresAuth:true,requiresBinding:true,title:'确认兑换'}, name:'member-points-checkout', path:'/points-shop/checkout/:id' },
    { component:()=>import('@/views/points/MemberPointsOrdersView.vue'), meta:{requiresAuth:true,requiresBinding:true,title:'我的兑换'}, name:'member-points-orders', path:'/points-shop/orders' },
    { component:()=>import('@/views/points/MemberPointsOrderDetailView.vue'), meta:{requiresAuth:true,requiresBinding:true,title:'兑换详情'}, name:'member-points-order-detail', path:'/points-shop/orders/:orderNo' },
    { component:()=>import('@/views/points/MemberPointsLotteryView.vue'), meta:{requiresAuth:true,requiresBinding:true,title:'积分抽奖'}, name:'member-points-lottery', path:'/points-shop/lottery' },
    { component:()=>import('@/views/points/MemberChannelRedemptionView.vue'), meta:{requiresAuth:true,requiresBinding:true,title:'渠道兑换'}, name:'member-channel-redemption', path:'/points-shop/channel' },
    { component:()=>import('@/views/booking/MemberBookingCreateView.vue'), meta:{requiresAuth:true,requiresBinding:true,title:'预约服务'}, name:'member-booking-create', path:'/booking' },
    {
      component: () => import('@/views/booking/MemberBookingListView.vue'),
      meta: { requiresAuth: true, requiresBinding: true, title: '我的预约' },
      name: 'member-booking-requests',
      path: '/booking/requests',
    },
    {
      component: () => import('@/views/booking/MemberBookingDetailView.vue'),
      meta: { requiresAuth: true, requiresBinding: true, title: '预约详情' },
      name: 'member-booking-detail',
      path: '/booking/requests/:requestNo',
    },
    {
      component: () => import('@/views/charity/MemberCharityView.vue'),
      meta: {
        public: appEnv.allowNonWechatAuth,
        requiresAuth: !appEnv.allowNonWechatAuth,
        requiresBinding: false,
        title: '公益计划',
      },
      name: 'member-charity',
      path: '/charity',
    },
    {
      component: () => import('@/views/service/MemberServiceLandingView.vue'),
      meta: { public: true, title: '联系我们' },
      name: 'member-contact',
      path: '/contact',
      props: { serviceCode: 'contact' },
    },
    {
      component: () => import('@/views/service/MemberServiceLandingView.vue'),
      meta: { public: true, title: '简介｜服务' },
      name: 'member-about',
      path: '/about',
      props: { serviceCode: 'about' },
    },
    {
      component: () => import('@/views/gift-card/MemberGiftCardView.vue'),
      meta: { requiresAuth: true, title: '礼品卡激活' },
      name: 'member-gift-card',
      path: '/gift-cards',
    },
    {
      component: () => import('@/views/share/BookingShareView.vue'),
      meta: { public: true, title: '预订确认' },
      name: 'booking-share',
      path: '/booking-shares/:shareCode',
    },
    {
      component: () => import('@/views/auth/MemberPhoneLoginView.vue'),
      meta: { requiresAuth: true, title: 'PRESTIGE 专属管家' },
      name: 'member-login',
      path: '/login',
    },
    {
      component: () => import('@/views/onboarding/OnboardingApplyView.vue'),
      meta: { requiresAuth: true, title: 'PRESTIGE 专属管家' },
      name: 'onboarding-apply',
      path: '/join',
    },
    {
      component: () => import('@/views/onboarding/OnboardingResultView.vue'),
      meta: { requiresAuth: true, title: 'PRESTIGE 专属管家' },
      name: 'onboarding-result',
      path: '/join/result',
    },
    {
      component: () => import('@/views/onboarding/MemberReferralView.vue'),
      meta: { requiresAuth: true, requiresBinding: true, title: '推荐入会' },
      name: 'member-referral',
      path: '/member/referral',
    },
    {
      component: () => import('@/views/auth/AuthResetView.vue'),
      meta: { public: true, title: '重新测试微信授权' },
      name: 'auth-reset',
      path: '/auth/reset',
    },
    {
      component: () => import('@/views/auth/AuthRedirectView.vue'),
      meta: { public: true, title: '微信授权' },
      name: 'auth-redirect',
      path: '/auth/redirect',
    },
    {
      component: () => import('@/views/auth/AuthCallbackView.vue'),
      meta: { public: true, title: '授权处理中' },
      name: 'auth-callback',
      path: '/auth/callback',
    },
    {
      component: () => import('@/views/auth/AuthErrorView.vue'),
      meta: { public: true, title: '暂时无法访问' },
      name: 'auth-error',
      path: '/auth/error',
    },
    {
      redirect: '/',
      path: '/:pathMatch(.*)*',
    },
  ],
  scrollBehavior: () => ({ top: 0 }),
})

router.beforeEach(async (to) => {
  document.title = `${String(to.meta.title || '会员服务')}`
  let session = readPortalSession()
  if (!to.meta.requiresAuth) return true

  if (!session?.accessToken) {
    return {
      name: 'auth-redirect',
      query: {
        authorizationMode: to.meta.requiresBinding || to.name === 'member-login' || to.name === 'onboarding-apply'
          ? 'complete'
          : 'basic',
        returnTo: to.fullPath,
      },
    }
  }

  if (to.name === 'onboarding-apply') {
    try {
      const entry = await resolveOnboardingEntry(session)
      session = entry.session
      useAuthStore().setSession(session)
      if (entry.target === 'result') return { name: 'onboarding-result' }
    } catch {
      return { name: 'auth-error', query: { reason: 'unavailable' } }
    }
    if (session.bindingStatus === 'bound') {
      return { name: 'member-home' }
    }
    if (session.authorizationScope !== 'snsapi_userinfo') {
      return {
        name: 'auth-redirect',
        query: { authorizationMode: 'complete', returnTo: to.fullPath },
      }
    }
    return true
  }

  if (to.name === 'member-login') {
    if (session.bindingStatus === 'pending') {
      return { name: 'onboarding-result' }
    }
    if (session.bindingStatus === 'bound') {
      return isSafePortalBackTarget(router, to, to.query.returnTo)
        ? String(to.query.returnTo)
        : { name: 'member-home' }
    }
    if (session.authorizationScope !== 'snsapi_userinfo') {
      return {
        name: 'auth-redirect',
        query: { authorizationMode: 'complete', returnTo: to.fullPath },
      }
    }
    return true
  }

  if (to.meta.requiresBinding) {
    if (session.bindingStatus === 'bound') return true
    if (session.bindingStatus === 'pending') return { name: 'onboarding-result' }
    return { name: 'member-login', query: { returnTo: to.fullPath } }
  }

  return true
})

router.onError((error, to) => {
  if (!isRouteChunkLoadError(error)) return
  const targetHref = router.resolve(to).href
  if (sessionStorage.getItem(ROUTE_CHUNK_RELOAD_KEY) === targetHref) {
    sessionStorage.removeItem(ROUTE_CHUNK_RELOAD_KEY)
    return
  }
  sessionStorage.setItem(ROUTE_CHUNK_RELOAD_KEY, targetHref)
  replaceWithFreshDocument(targetHref)
})

router.afterEach((to, _from, failure) => {
  if (failure) return
  const targetHref = router.resolve(to).href
  if (sessionStorage.getItem(ROUTE_CHUNK_RELOAD_KEY) === targetHref) {
    sessionStorage.removeItem(ROUTE_CHUNK_RELOAD_KEY)
  }
})

window.addEventListener('member-portal:unauthorized', () => {
  if (router.currentRoute.value.meta.public) return
  void router.replace({
    name: 'auth-redirect',
    query: {
      authorizationMode: router.currentRoute.value.meta.requiresBinding ? 'complete' : 'basic',
      returnTo: router.currentRoute.value.fullPath,
    },
  })
})

export default router
